import { breakpointSizes, getBreakpointDown, getFontCss, styleConstants, themeConstants } from '@paywithextend/components'
import moment, { Moment } from 'moment'
import React from 'react'
import DateRangePickerFieldsDropdown from 'src/components/DateRangePickerFieldsDropdown/DateRangePickerFieldsDropdown'
import RadioButton from 'src/components/RadioButton/RadioButton'
import Tooltip from 'src/components/Tooltip/Tooltip'
import Trns from 'src/localization/Trns'
import { TransactionDateType } from 'src/models/transaction.models'
import { css } from 'styled-components'

interface TransactionDateTypeFilterProps {
  dateType: TransactionDateType
  setDateType: (dateType: TransactionDateType) => void
  startDateFilter: Moment | undefined
  setStartDateFilter: (date: Moment | undefined) => void
  endDateFilter: Moment | undefined
  setEndDateFilter: (date: Moment | undefined) => void
  filterValues: {
    since?: Moment
    until?: Moment
  }
  isTransactionsFetching?: boolean
  showDateRange?: boolean
}

const TransactionDateTypeFilter: React.FC<TransactionDateTypeFilterProps> = ({
  dateType,
  setDateType,
  startDateFilter,
  setStartDateFilter,
  endDateFilter,
  setEndDateFilter,
  filterValues,
  isTransactionsFetching,
  showDateRange = false,
}) => {
  const radioDateTypes = [
    {
      label: 'Transaction Date',
      subLabel: 'Date on which transactions were authorized by the cardholder',
      value: TransactionDateType.Transaction,
    },
    {
      label: 'Clear Date',
      subLabel:
        'Date on which transactions were processed by the issuer (cleared), also known as the Settlement Date or Posted Date',
      value: TransactionDateType.Clear,
    },
  ]

  return (
    <>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 4px;
        `}
      >
        <div
          css={css`
            ${getFontCss(
              14,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )};
          `}
        >
          <Trns source={'Date Type'} />
        </div>
        <Tooltip
          openLeft={false}
          css={css`
            padding-right: 5px;
            z-index: 2;
          `}
        >
          <p>
            <Trns
              source={
                '${boldedText} is the date on which transactions were authorized by the cardholder.'
              }
              expressions={{
                boldedText: (
                  <span
                    css={css`
                      ${getFontCss(
                        12,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.medium,
                      )};
                      ${getBreakpointDown(breakpointSizes.XSM)(css`
                        ${getFontCss(
                          16,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.medium,
                        )};
                      `)}
                    `}
                  >
                    <Trns source={'Transaction Date'} />
                  </span>
                ),
              }}
            />
            <span
              css={css`
                display: block;
                margin-top: 10px;
              `}
            >
              <Trns
                source={
                  '${boldedText} is the date on which transactions were processed by the issuer (cleared), also known as the Settlement Date or Posted Date.'
                }
                expressions={{
                  boldedText: (
                    <span
                      css={css`
                        ${getFontCss(
                          12,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.medium,
                        )};
                        ${getBreakpointDown(breakpointSizes.XSM)(css`
                          ${getFontCss(
                            16,
                            themeConstants.colors.font_primary,
                            styleConstants.fontFamilies.medium,
                          )};
                        `)}
                      `}
                    >
                      <Trns source={'Clear Date'} />
                    </span>
                  ),
                }}
              />
            </span>
          </p>
        </Tooltip>
      </div>
      {radioDateTypes.map((item, i) => (
        <div
          key={i}
          css={css`
            display: flex;
            align-items: flex-start;
            margin-top: 12px;
          `}
        >
          <RadioButton
            onClick={(): void => {
              setDateType(item.value)
            }}
            isSelected={dateType === item.value}
            css={css`
              display: block;
            `}
          />
          <div
            css={css`
              margin-left: 20px;
            `}
          >
            <p
              css={css`
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )}
              `}
            >
              <Trns source={item.label} />
            </p>
          </div>
        </div>
      ))}
      {showDateRange ? (
        <div
          key={'transaction-date-range'}
          css={css`
            margin-top: 20px;
            display: flex;
            gap: 6px;
            ${getFontCss(
              14,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )};
          `}
        >
          <DateRangePickerFieldsDropdown
            id={'startDate'}
            since={
              startDateFilter ||
              filterValues.since ||
              moment.utc().subtract(30, 'days')
            }
            until={
              endDateFilter || filterValues.until || moment.utc()
            }
            setSince={setStartDateFilter}
            setUntil={setEndDateFilter}
            initialMonth={startDateFilter || moment.utc()}
            loading={isTransactionsFetching}
          />
        </div>
      ) : null}
    </>
  )
}

export default TransactionDateTypeFilter
